import React from 'react';
import type { StimulusTheme } from '@interactive-room';
import {
  parseNewsComparisonBundle,
  parseStructuredNewspaperArticle,
  shouldUseNewspaperColumns,
  splitLetterContent,
} from '@interactive-room';
import { PrintNewsComparison, PrintStructuredNewspaper } from './PrintNewsEditorial';
import { PrintRichText } from './PrintRichText';

export function PrintThemedReading({
  theme,
  title,
  content,
  className = '',
}: {
  theme: StimulusTheme;
  title?: string;
  content: string;
  className?: string;
}) {
  const trimmed = content.trim();
  if (!trimmed) return null;

  const comparison = parseNewsComparisonBundle(trimmed);
  if (comparison) {
    return <PrintNewsComparison bundle={comparison} className={className} />;
  }

  switch (theme) {
    case 'newspaper': {
      const structured = parseStructuredNewspaperArticle(trimmed);
      if (structured) {
        const withTitle =
          !structured.outlet && title ? { ...structured, outlet: title } : structured;
        // Prefer title as headline only when structured lacks one from fields (rare).
        const article =
          title &&
          structured.headline === 'Noticia' &&
          !/ep[ií]grafe|titular/i.test(trimmed)
            ? { ...withTitle, headline: title }
            : withTitle;
        return <PrintStructuredNewspaper article={article} className={className} />;
      }

      const useColumns = shouldUseNewspaperColumns(trimmed);
      const dropCap = trimmed[0] ?? '';
      const rest = trimmed.slice(1);
      return (
        <div className={`print-themed-reading print-themed-reading--newspaper ${className}`.trim()}>
          {title ? <h4 className="print-stimulus__headline">{title}</h4> : null}
          <div
            className={`print-stimulus__body print-stimulus__body--newspaper${
              useColumns ? ' print-stimulus__body--cols-2' : ''
            }`}
          >
            {dropCap ? <span className="print-stimulus__drop-cap">{dropCap}</span> : null}
            <PrintRichText text={rest} />
          </div>
        </div>
      );
    }
    case 'letter': {
      const { dateLine, body } = splitLetterContent(trimmed);
      return (
        <div className={`print-themed-reading print-themed-reading--letter ${className}`.trim()}>
          {title ? <p className="print-stimulus__letter-salutation">{title}</p> : null}
          {dateLine ? <p className="print-stimulus__date">{dateLine}</p> : null}
          <div className="print-stimulus__body print-stimulus__body--letter">
            <PrintRichText text={body} />
          </div>
        </div>
      );
    }
    case 'poem':
      return (
        <div className={`print-themed-reading print-themed-reading--poem ${className}`.trim()}>
          {title ? <p className="print-stimulus__poem-title">{title}</p> : null}
          <blockquote className="print-stimulus__body print-stimulus__body--poem">
            <PrintRichText text={trimmed} />
          </blockquote>
        </div>
      );
    case 'document':
      return (
        <div className={`print-themed-reading print-themed-reading--document ${className}`.trim()}>
          {title ? <p className="print-stimulus__document-label">{title}</p> : null}
          <div className="print-stimulus__body print-stimulus__body--document">
            <PrintRichText text={trimmed} />
          </div>
        </div>
      );
    default: {
      // Prosa: si el cuerpo trae campos de noticia, maqueta gráfica de todos modos.
      const structured = parseStructuredNewspaperArticle(trimmed);
      if (structured) {
        return <PrintStructuredNewspaper article={structured} className={className} />;
      }
      return (
        <div className={`print-themed-reading print-themed-reading--prose ${className}`.trim()}>
          {title ? <p className="print-stimulus__label">{title}</p> : null}
          <div className="print-stimulus__body">
            <PrintRichText text={trimmed} />
          </div>
        </div>
      );
    }
  }
}
